/**
 * **콜팝 가맹점 웹 가입(`/join/callpop`) — 매장 정보 검사·주소 찾기·약관 줄·가입 본문·응답 읽기 한 곳.** 의존성 없는 순수 모듈이다(Vue·
 * Element·`@/` 별칭 금지 — 단위 시험이 node 에서 그대로 읽는다). 화면 문장은 `ownerCopy.js` 가 고른다.
 *
 * 정본: pos2-lite `src/domain/owner/owner-store-form.ts`(② 매장 정보)·`owner-input.ts`(`optionalEmailProblem`)·`owner-signup.ts`(가입
 * 본문)·`owner-terms.ts`(동의 줄)·`owner-wire.ts`(가입 결과·주소 결과)·`src/domain/phone-order/places.ts`(`addressSearchPlan`). 같은 가입
 * 규칙을 POS2 Lite·오더엔 멀티·관리 웹이 함께 쓴다. 리포·언어가 달라 추출하지 않는다 — 정본을 고치면 이 사본과 골든
 * (`tests/unit/ownerSignup.test.js`)을 같이 고친다. 최종 판정은 서버(`OwnerSignupService`·`StoreProfile`)다 — 화면은 사람이 서버에 가기 전에
 * 고칠 수 있게 먼저 볼 뿐이다.
 *
 * 관리 웹이 다른 것(계획 `docs/planning/callpop-web-signup-20261029.md` §11.2):
 * - 칸 둘을 더한다 — 매장 전화번호(필수, W8 · 서버 `TEL_REQUIRED`)와 관리점 코드(선택, W4). 관리점 코드의 형식·정규화는 서버
 *   `AgencyCodeRules` 가 정본이라(대소문자·하이픈·공백·3자리 일련번호를 받는다) 화면은 앞뒤 공백만 걷는다.
 * - 본문에 `channel: CALLPOP_WEB` 를 싣는다. 제품은 `POS2LITE` 다(W3·W9 — 요금·노출·사장님 계정의 뜻이 셀프 가입과 같다).
 * - 약관 판 표가 비었거나 못 읽으면 정본의 상수 목록(`OWNER_SIGNUP_FALLBACK_TERMS`)으로 대신하지 않는다 — 그 판으로 보내면 서버가 400 이다
 *   (계획 §10 「약관 판」). 화면이 다시 읽게 한다.
 * - 동의 체크는 `Set` 대신 열쇠 배열로 든다(Vue 2 는 `Set` 을 반응형으로 만들지 않는다) — 열쇠·뜻은 정본과 같다.
 */
// 확장자를 적는 이유: 단위 시험이 앱 번들 밖(node)에서 읽는다 — node ESM 은 상대 경로 확장자를 추론하지 않는다.
import { isEmailAddress } from './ownerLogin.js'
import { TEL_KIND, telDigits, telKindOf } from './telNumber.js'

/** 공개 가입 화면의 주소 — 아임웹·콜팝 홈페이지가 거는 링크(계획 §11.9) */
export const CALLPOP_JOIN_PATH = '/join/callpop'

/** 가입하는 제품 — 콜팝 웹 가입도 POS2 Lite 다(W3·W9) */
export const SIGNUP_PRODUCT = 'POS2LITE'

/** 가입 경로 — 서버가 이 값을 보고 「콜팝」 사업장·관리점 매핑·신청 행을 함께 만든다(§11.1) */
export const SIGNUP_CHANNEL = 'CALLPOP_WEB'

/** 약관 판을 읽는 걸음 — `GET /api/owner/terms?product=&stage=` */
export const SIGNUP_TERMS_STAGE = 'SIGNUP'

/** 가입의 세 걸음 — 화면의 단계 점과 오류가 돌아갈 자리 */
export const SIGNUP_STEP = Object.freeze({ IDENTITY: 1, STORE: 2, FINISH: 3 })

// ─── ② 매장 정보(정본: owner-store-form.ts) ────────────────────────────────────────────────────────

/** 업종 — 서버 `StoreProfile` 어휘 */
export const BUSINESS_TYPES = Object.freeze(['RESTAURANT', 'CAFE_DESSERT', 'OTHER'])

/** 운영 방식 — 서버 어휘, 이 순서로 보낸다 */
export const OPERATION_MODES = Object.freeze(['DELIVERY', 'PACKING', 'HALL'])

/** 다른 배달 주문 프로그램(타사 솔루션) */
export const OTHER_PROGRAMS = Object.freeze(['USING', 'NONE', 'UNKNOWN'])

export const STORE_NAME_MAX = 50
export const CEO_NAME_MAX = 20
export const ADDRESS_DETAIL_MAX = 50

/** 매장 전화번호 칸의 글자 상한 — 매장 표 `tel` 칸(관리 웹 매장 등록 화면과 같은 20자) */
export const STORE_TEL_MAX = 20

/** 관리점 코드 칸의 글자 상한 — 사람이 불러 준 표기(`cj-0001`)까지 받는다. 형식은 서버가 본다 */
export const AGENCY_CODE_MAX = 20

/**
 * 빈 매장 정보 — 대표자명은 본인확인 이름이 있으면 그 이름으로 시작한다(가족·지인 명의면 고친다 — 정본 D4). 업종은 「일반음식점」이 골라진
 * 채 시작한다. `tel`·`agencyCode` 는 관리 웹(콜팝 웹 가입)만 더하는 칸이다.
 *
 * @param {?string} verifiedName
 */
export function emptyStoreDraft(verifiedName) {
  return {
    busiNum: '',
    storeNm: '',
    ceoNm: verifiedName || '',
    tel: '',
    businessType: 'RESTAURANT',
    address: null,
    addrDtl: '',
    operationModes: [],
    cardPayment: null,
    otherProgram: null,
    agencyCode: '',
  }
}

/** 매장 정보의 문제 이름 — 문장은 `ownerCopy.ownerStoreDraftText`. 화면의 칸 순서다 */
export const STORE_DRAFT_PROBLEM = Object.freeze({
  BIZ_NUM: 'BIZ_NUM',
  STORE_NM: 'STORE_NM',
  CEO_NM: 'CEO_NM',
  TEL: 'TEL',
  TEL_INVALID: 'TEL_INVALID',
  ADDRESS: 'ADDRESS',
  ADDRESS_DETAIL: 'ADDRESS_DETAIL',
  MODES: 'MODES',
  CARD: 'CARD',
})

/**
 * 매장 전화로 받는 번호 종류 — 고객 기록·매장 전화가 같이 쓰는 번호 표(`telNumber`)에서 알아보는 번호(뒷 4자리·빈 값·알 수 없는 번호 밖).
 * 서버(`StoreProfile` — 매장 저장의 `REGEXP_TEL`)는 숫자·하이픈이면 받는다. 화면은 사람이 고칠 수 있게 그 칸에서 먼저 말한다
 */
const STORE_TEL_KINDS = Object.freeze([
  TEL_KIND.MOBILE,
  TEL_KIND.OLD_MOBILE,
  TEL_KIND.LANDLINE,
  TEL_KIND.VOIP,
  TEL_KIND.SAFE,
  TEL_KIND.REPRESENTATIVE,
])

/** 문제가 가리키는 칸 — 그 칸 밑에 말한다(그 밖의 문제는 폼 아래 한 줄). 칸이 아니면 `null` */
export function storeDraftFieldOf(problem) {
  return problem === STORE_DRAFT_PROBLEM.TEL || problem === STORE_DRAFT_PROBLEM.TEL_INVALID ? 'tel' : null
}

/** 국세청 검증번호의 가중치 — 앞 9자리에 차례로 곱한다 */
const BIZ_NO_CHECK_WEIGHTS = Object.freeze([1, 3, 7, 1, 3, 7, 1, 3, 5])

/** 시험용 사업자등록번호의 앞 3자리 — 서버 `BizNoRules.isTestNumber`(사용자 결정 2026-10-30). 서버는 이 번호를 국세청에도 묻지 않는다 */
const TEST_BIZ_NO_PREFIX = '999'

/**
 * 가입의 사업자등록번호 — 숫자 10자리 → 시험용 번호(앞 3자리 999)면 통과 → 국세청 검증번호 → 더미(열 자리가 모두 같다·앞 3자리가 000) 아님.
 * 서버 정본 `BizNoRules.accept` 와 같은 규칙·같은 순서이고 골든 벡터도 정본 시험과 같은 목록이다. 하이픈·띄어쓰기는 무시한다.
 */
export function isValidBizNo(value) {
  const bizNo = telDigits(value)
  if (bizNo.length !== 10) return false
  if (bizNo.startsWith(TEST_BIZ_NO_PREFIX)) return true
  const digits = Array.from(bizNo).map(Number)
  const sum =
    BIZ_NO_CHECK_WEIGHTS.reduce((total, weight, index) => total + digits[index] * weight, 0) +
    Math.floor((digits[8] * 5) / 10)
  if ((10 - (sum % 10)) % 10 !== digits[9]) return false
  const dummy = Array.from(bizNo).every((digit) => digit === bizNo[0]) || bizNo.startsWith('000')
  return !dummy
}

/**
 * 위에서부터 첫 문제 하나 — 화면은 그 문구 한 줄을 보인다(정본 순서에 매장 전화번호를 대표자명 다음 칸으로 끼운다). 문제가 없으면 `null`.
 *
 * @returns {?string} [STORE_DRAFT_PROBLEM] 의 하나
 */
export function ownerStoreDraftProblem(draft) {
  if (!isValidBizNo(draft.busiNum)) return STORE_DRAFT_PROBLEM.BIZ_NUM
  const storeNm = textOf(draft.storeNm).trim()
  if (storeNm.length === 0 || storeNm.length > STORE_NAME_MAX) return STORE_DRAFT_PROBLEM.STORE_NM
  const ceoNm = textOf(draft.ceoNm).trim()
  if (ceoNm.length === 0 || ceoNm.length > CEO_NAME_MAX) return STORE_DRAFT_PROBLEM.CEO_NM
  const tel = telDigits(draft.tel)
  if (tel.length === 0) return STORE_DRAFT_PROBLEM.TEL
  if (STORE_TEL_KINDS.indexOf(telKindOf(tel)) < 0) return STORE_DRAFT_PROBLEM.TEL_INVALID
  if (draft.address === null) return STORE_DRAFT_PROBLEM.ADDRESS
  if (textOf(draft.addrDtl).trim().length > ADDRESS_DETAIL_MAX) return STORE_DRAFT_PROBLEM.ADDRESS_DETAIL
  if (draft.operationModes.length === 0) return STORE_DRAFT_PROBLEM.MODES
  if (draft.cardPayment === null) return STORE_DRAFT_PROBLEM.CARD
  return null
}

/**
 * 서버 본문의 `store` — 정본 모양(숫자만 사업자번호, 다듬은 이름, 서버 어휘 순서의 운영 방식, 다른 프로그램은 골랐을 때만)에 `tel`(숫자만 —
 * 띄어쓰기를 남기면 서버 400) 을 더한다.
 * 검사를 지나지 않은 초안이면 `null`.
 */
export function ownerStoreRequestOf(draft) {
  if (ownerStoreDraftProblem(draft) !== null) return null
  const request = {
    busiNum: telDigits(draft.busiNum),
    storeNm: draft.storeNm.trim(),
    ceoNm: draft.ceoNm.trim(),
    tel: telDigits(draft.tel),
    businessType: draft.businessType,
    address: { zipCd: draft.address.zipCd, addr: draft.address.addr, addrDtl: draft.addrDtl.trim() },
    operationModes: OPERATION_MODES.filter((mode) => draft.operationModes.indexOf(mode) > -1),
    cardPayment: draft.cardPayment,
  }
  if (draft.otherProgram !== null) request.otherProgram = draft.otherProgram
  return request
}

/** 운영 방식 고르기 — 있으면 빼고 없으면 넣는다(새 배열) */
export function toggleOperationMode(modes, mode) {
  return modes.indexOf(mode) > -1 ? modes.filter((value) => value !== mode) : modes.concat([mode])
}

/** 다른 프로그램 고르기 — 같은 답을 다시 누르면 푼다(선택 칸) */
export function toggleOtherProgram(current, program) {
  return current === program ? null : program
}

/** 사업자등록번호 치는 중 — 000-00-00000 */
export function formatBizNoTyping(value) {
  const digits = telDigits(value).slice(0, 10)
  if (digits.length <= 3) return digits
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
}

/** 가입 ①의 이메일(선택) — 비었으면 문제없음. 문제면 `'EMAIL'` */
export function optionalEmailProblem(email) {
  const text = textOf(email)
  return text.trim().length === 0 || isEmailAddress(text) ? null : 'EMAIL'
}

// ─── 주소 찾기(정본: places.ts `addressSearchPlan` · owner-wire.ts `readOwnerAddressPage`) ─────────────────

/**
 * 찾는 말을 서버에 물을까 — 짧거나(2자 미만) 숫자뿐이거나 숫자 없이 낱자 자음(초성)이 섞였으면 묻지 않는다(전화주문 주소 검색과 같은 규칙).
 *
 * @returns {{remote: boolean}}
 */
export function addressSearchPlan(query) {
  const compact = textOf(query).replace(/\s/g, '')
  if (Array.from(compact).length < 2 || /^\d+$/.test(compact)) return { remote: false }
  return { remote: !(/[ㄱ-ㅎ]/.test(compact) && !/\d/.test(compact)) }
}

/** 주소 찾기 본문 — `POST /api/owner/address-search` `{ticket, addressQuery, page}`(가입은 본인확인 표로 찾는다) */
export function addressSearchBody(ticket, query, page) {
  return { ticket, addressQuery: textOf(query).trim(), page }
}

/** 주소 찾기 200 `{results:[{zipCd, roadAddr, jibunAddr}], hasMore}` → 같은 모양. 형식 밖이면 `null` */
export function ownerAddressPageOf(body) {
  const record = plainRecord(body)
  if (!record || !Array.isArray(record.results)) return null
  const results = []
  for (const raw of record.results) {
    const row = plainRecord(raw)
    if (!row || typeof row.zipCd !== 'string' || typeof row.roadAddr !== 'string') return null
    results.push({
      zipCd: row.zipCd,
      roadAddr: row.roadAddr,
      jibunAddr: typeof row.jibunAddr === 'string' ? row.jibunAddr : '',
    })
  }
  return { results, hasMore: record.hasMore === true }
}

/**
 * **주소 조회 단위** — 제출한 검색어·지금까지 받은 결과·마지막 쪽·더 있음을 한 덩어리로 둔다. 화면은 **성공한 응답으로만 이 단위를 통째로**
 * 바꾼다 — 실패하면 앞 단위 그대로라 [더 보기]는 늘 보이는 결과의 검색어를 잇는다(리뷰 2회 — 검색어만 바뀌고 결과·쪽은 앞 것이던 결함). 아직
 * 찾지 않았으면 [EMPTY_ADDRESS_LOOKUP]
 */
export const EMPTY_ADDRESS_LOOKUP = Object.freeze({ searched: '', results: Object.freeze([]), page: 0, hasMore: false })

/** [주소 찾기]의 요청 — 그 검색어의 1쪽, 앞 결과 없음 */
export function addressFirstPageOf(searched) {
  return { searched: textOf(searched).trim(), page: 1, before: [] }
}

/** [더 보기]의 요청 — 그 단위의 검색어 다음 쪽, 그 단위의 결과 뒤에 잇는다 */
export function addressNextPageOf(unit) {
  return { searched: unit.searched, page: unit.page + 1, before: unit.results }
}

/** 요청과 그 성공 응답(`ownerAddressPageOf`) → 새 단위(인자를 바꾸지 않는다) */
export function addressLookupOf(request, found) {
  return {
    searched: request.searched,
    results: request.before.concat(found.results),
    page: request.page,
    hasMore: found.hasMore,
  }
}

// ─── ③ 약관(정본: owner-terms.ts) ──────────────────────────────────────────────────────────────────

/** 약관 판 질의 — 가입 걸음의 지금 판 */
export function signupTermsQuery() {
  return { product: SIGNUP_PRODUCT, stage: SIGNUP_TERMS_STAGE }
}

/** 한 응답의 문서 수 상한·코드·판·제목 글자 상한 — 정본과 같다 */
const MAX_TERMS_DOCS = 20
const MAX_TERMS_KEY_LENGTH = 64
const MAX_TERMS_TITLE_LENGTH = 200

function termsKey(value) {
  return typeof value === 'string' && value.length > 0 && value.length <= MAX_TERMS_KEY_LENGTH ? value : null
}

function termsDocOf(raw) {
  const record = plainRecord(raw)
  if (!record) return null
  const code = termsKey(record.code)
  const version = termsKey(record.version)
  const title = record.title
  if (
    code === null ||
    version === null ||
    typeof title !== 'string' ||
    title.length === 0 ||
    title.length > MAX_TERMS_TITLE_LENGTH ||
    typeof record.required !== 'boolean'
  ) {
    return null
  }
  // 본문·바뀜은 없으면 빈 글·거짓이다. 있는데 모양이 틀리면 형식 밖이다
  if (record.body !== undefined && record.body !== null && typeof record.body !== 'string') return null
  if (record.changed !== undefined && typeof record.changed !== 'boolean') return null
  return {
    code,
    version,
    title,
    required: record.required,
    body: typeof record.body === 'string' ? record.body : '',
    changed: record.changed === true,
  }
}

/**
 * 가입 걸음의 약관 판 `{docs:[{code, version, title, required, body, changed}]}` → 문서 목록. 판 표가 비었거나 형식 밖(문서 하나라도 틀림
 * 포함)이면 `null` — 화면은 다시 읽게 한다(상수 목록으로 대신하지 않는다 — 머리말).
 */
export function signupTermsOf(body) {
  const record = plainRecord(body)
  const docs = record && record.docs
  if (!Array.isArray(docs) || docs.length === 0 || docs.length > MAX_TERMS_DOCS) return null
  const read = docs.map(termsDocOf)
  return read.some((doc) => doc === null) ? null : read
}

/** 동의 체크의 열쇠 — 문서와 판에 묶는다. 판이 바뀐 줄은 같은 코드여도 고르지 않은 것이다 */
export function termsAgreementKey(doc) {
  return `${doc.code}@${doc.version}`
}

/** 이 줄(이 판)을 골랐나 — `agreed` 는 열쇠 배열 */
export function termsAgreed(agreed, doc) {
  return agreed.indexOf(termsAgreementKey(doc)) > -1
}

/** 필수 문서를 모두 골랐나 */
export function termsRequiredAgreed(docs, agreed) {
  return docs.every((doc) => !doc.required || termsAgreed(agreed, doc))
}

/** 「전체 동의」의 체크 — 문서를 모두 골랐나 */
export function termsAllAgreed(docs, agreed) {
  return docs.length > 0 && docs.every((doc) => termsAgreed(agreed, doc))
}

/** 「전체 동의」 — 모두 골랐으면 모두 빼고, 아니면 모두 고른다(새 배열) */
export function toggleAllTerms(docs, agreed) {
  return termsAllAgreed(docs, agreed) ? [] : docs.map(termsAgreementKey)
}

/** 한 줄 고르기·빼기(새 배열) */
export function toggleTerm(agreed, doc) {
  const key = termsAgreementKey(doc)
  return agreed.indexOf(key) > -1 ? agreed.filter((value) => value !== key) : agreed.concat([key])
}

/** 동의 줄 — 보인 문서 모두를 동의 여부와 함께 받은 판으로 싣는다(고르지 않은 선택 문서도 이력이다) */
export function termsConsents(docs, agreed) {
  return docs.map((doc) => ({ code: doc.code, version: doc.version, agreed: termsAgreed(agreed, doc) }))
}

// ─── 가입 본문과 답(정본: owner-signup.ts · owner-wire.ts) ────────────────────────────────────────────

/**
 * 가입 본문 — `POST /api/owner/signup`. 정본 모양(`ticket`·`idempotencyKey`·`product`·`email?`·`store`·`password`·`consents`)에 콜팝 경로 칸
 * (`channel`·`agencyCode?`)을 더한다. 빈 이메일·빈 관리점 코드는 싣지 않는다. 이메일은 소문자로 보낸다.
 *
 * @param {{ticket: string, idempotencyKey: string, email: string, store: object, agencyCode: string, password: string,
 *   terms: Array<object>, agreed: Array<string>}} input
 */
export function ownerSignupBody(input) {
  const email = textOf(input.email).trim().toLowerCase()
  const agencyCode = textOf(input.agencyCode).trim()
  const body = {
    ticket: input.ticket,
    idempotencyKey: input.idempotencyKey,
    product: SIGNUP_PRODUCT,
    channel: SIGNUP_CHANNEL,
    store: input.store,
    password: input.password,
    consents: termsConsents(input.terms, input.agreed),
  }
  if (email.length > 0) body.email = email
  if (agencyCode.length > 0) body.agencyCode = agencyCode
  return body
}

/**
 * 가입 201 `{loginId, email, loginTicket, store{storeCd, storeId, storeNm}, storeCount, applicationId}` → 화면이 쓰는 칸. 관리 웹은 로그인
 * 표(`loginTicket`)로 곧바로 사장님 세션을 연다(§11.2) — 표·매장이 없으면 형식 밖(`null`).
 */
export function ownerSignupResultOf(body) {
  const record = plainRecord(body)
  const store = record && plainRecord(record.store)
  const storeCd = store && positiveInteger(store.storeCd)
  if (!record || !storeCd || typeof record.loginId !== 'string' || !hasText(record.loginTicket)) return null
  if (typeof store.storeId !== 'string' || typeof store.storeNm !== 'string') return null
  return {
    loginId: record.loginId,
    email: hasText(record.email) ? record.email : null,
    loginTicket: record.loginTicket,
    store: { storeCd, storeId: store.storeId, storeNm: store.storeNm },
    applicationId: positiveInteger(record.applicationId),
  }
}

/**
 * 거절이 돌려보낼 걸음(`ownerCopy.ownerSignupRejection` 의 `step` — 정본 `OwnerSignupStepToFix`) → 화면의 걸음. 본인확인·이메일은 ①,
 * 매장 정보는 ②, 비밀번호·약관은 ③. 모르면(`null` — 그 자리에 머문다) `null`.
 *
 * @param {?string} stepToFix `IDENTITY`·`EMAIL`·`STORE`·`PASSWORD`·`TERMS`
 * @returns {?number} [SIGNUP_STEP] 의 하나
 */
export function signupStepOf(stepToFix) {
  switch (stepToFix) {
    case 'IDENTITY':
    case 'EMAIL':
      return SIGNUP_STEP.IDENTITY
    case 'STORE':
      return SIGNUP_STEP.STORE
    case 'PASSWORD':
    case 'TERMS':
      return SIGNUP_STEP.FINISH
    default:
      return null
  }
}

function plainRecord(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null
}

function positiveInteger(value) {
  if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) return value
  if (typeof value === 'string' && /^\d{1,15}$/.test(value)) {
    const parsed = Number(value)
    return parsed > 0 ? parsed : null
  }
  return null
}

function textOf(value) {
  return value == null ? '' : String(value)
}

function hasText(value) {
  return typeof value === 'string' && value.trim() !== ''
}
